"use strict";

/*
 * Slider Helper helps synchronize the values between a text/number input and the range slider,
 *   as well as handle the change events and provide a cleaner way to set and retrieve UI control values
 */
import {ManagerEvent} from './calculator-helpers';

export default class SliderHelper {
    events = new EventTarget();
    disabled = false;

    constructor(inputId, sliderId, clearButtonId, sliderValueLabelId, lowerLimitLabelId, upperLimitLabelId, changeFunction) {
        this.inputId = inputId;
        this.sliderId = sliderId;
        this.clearButtonId = clearButtonId;
        this.sliderValueLabelId = sliderValueLabelId;
        this.lowerLimitLabelId = lowerLimitLabelId;
        this.upperLimitLabelId = upperLimitLabelId;
        this.changeFunction = changeFunction;
        this.linkInput(inputId);
        this.linkSlider(sliderId);
        this.linkClearButton(clearButtonId);
        this.linkLimitLabels(lowerLimitLabelId, upperLimitLabelId);
        this.setChangeFunction(changeFunction);

        this.sliderValueLabel = document.getElementById(sliderValueLabelId);
    }

    linkInput(inputId) {
        // If this already has an input linked, remove the listeners and classes before linking the new one
        if (this.input) {
            this.input.removeEventListener('input', this.handleInputChanged.bind(this));
            this.input.removeEventListener('change', this.handleInputChanged.bind(this));
            this.input.classList.remove('input');
            this.input.classList.remove(this.input.id);
        }

        this.input = document.getElementById(inputId);

        // If the input element was found, assign the classes and event listeners
        if (this.input) {
            this.input.classList.add('input');
            this.input.classList.add(inputId);

            // Just a 'double check' to make sure we're not already listening to this element, although should never happen
            this.input.removeEventListener('input', this.handleInputChanged.bind(this));
            this.input.removeEventListener('change', this.handleInputChanged.bind(this));

            this.input.addEventListener('input', this.handleInputChanged.bind(this));
            this.input.addEventListener('change', this.handleInputChanged.bind(this));

            this.disabled = this.input.disabled;
        }
    }

    linkSlider(sliderId) {
        // If this already has a slider linked, remove the listeners and classes before linking the new one
        if (this.slider) {
            this.slider.removeEventListener('click', this.handleSliderClicked.bind(this));
            this.slider.removeEventListener('input', this.handleSliderChanged.bind(this));
            this.slider.removeEventListener('change', this.handleSliderChanged.bind(this));
            this.slider.classList.remove('slider');
            this.slider.classList.remove(this.slider.id);
        }

        this.slider = document.getElementById(sliderId);

        // If the slider element was found, assign the classes and event listeners
        if (this.slider) {
            this.slider.classList.add('slider');
            this.slider.classList.add(sliderId);

            // Just a 'double check' to make sure we're not already listening to this element, although should never happen
            this.slider.removeEventListener('click', this.handleSliderClicked.bind(this));
            this.slider.removeEventListener('input', this.handleSliderChanged.bind(this));
            this.slider.removeEventListener('change', this.handleSliderChanged.bind(this));

            this.slider.addEventListener('click', this.handleSliderClicked.bind(this));
            this.slider.addEventListener('input', this.handleSliderChanged.bind(this));
            this.slider.addEventListener('change', this.handleSliderChanged.bind(this));

            this.disabled = this.slider.disabled;
        }
    }

    linkClearButton(clearButtonId) {
        // If this already has a clear button linked, remove the listeners and classes before linking the new one
        if (this.clearButton) {
            this.clearButton.removeEventListener('click', this.handleClearButtonClicked.bind(this));
            this.clearButton.classList.remove('clearButton');
            this.clearButton.classList.remove(this.clearButton.id);
        }

        this.clearButton = document.getElementById(clearButtonId);

        // If the clearButton element was found, assign the classes and event listeners
        if (this.clearButton) {
            this.clearButton.classList.add('clearButton');
            this.clearButton.classList.add(clearButtonId);

            // Just a 'double check' to make sure we're not already listening to this element, although should never happen
            this.clearButton.removeEventListener('click', this.handleClearButtonClicked.bind(this));

            this.clearButton.addEventListener('click', this.handleClearButtonClicked.bind(this));
        }
    }

    linkLimitLabels(lowerLimitLabelId, upperLimitLabelId) {
        this.lowerLimitLabel = document.getElementById(lowerLimitLabelId);
        this.upperLimitLabel = document.getElementById(upperLimitLabelId);
    }

    /*
     * Set the input and slider min, max, and step. This is more relevant to sliders,
     *   but some browsers respect this on number text inputs as well.
     */
    setRange(min, max, step) {
        this.min = min;
        this.max = max;
        this.step = step;

        if (this.slider) {
            this.slider.min = min;
            this.slider.max = max;
            this.slider.step = step;
        }

        if (this.input) {
            this.input.min = min;
            this.input.max = max;
            this.input.step = step;
        }

        if (this.lowerLimitLabel) {
            this.lowerLimitLabel.innerHTML = min;
        }

        if (this.upperLimitLabel) {
            this.upperLimitLabel.innerHTML = max;
        }
    }

    setLanguage({emptyLabel, disabledLabel, labelPrefix, labelSuffix}) {
        this.emptyLabel = emptyLabel;
        this.disabledLabel = disabledLabel;
        this.labelPrefix = labelPrefix;
        this.labelSuffix = labelSuffix;
    }

    setValue(newValue, event) {
        const oldValue = this.value;
        let fixedValue = parseFloat(newValue);

        // Check if the new value is valid
        if (newValue === undefined || newValue === null || newValue === '' || isNaN(fixedValue)) {
            // New value is empty or invalid, clear the UI control values
            this.value = undefined; // this.value is internal only, does not affect controller value unless read externally
            if (this.slider) {
                // Set the slider to the middle of the range
                this.slider.value = null; // Slider doesn't like undefined, but null is okay
                this.slider.classList.remove('value-set');
                this.slider.classList.add('value-unset');
            }
            if (this.input) {
                // Clear the text input value
                this.input.value = undefined;
                this.input.classList.remove('value-set');
                this.input.classList.add('value-unset');
            }
            if (this.clearButton) {
                this.clearButton.classList.remove('value-set');
                this.clearButton.classList.add('value-unset');
            }
            if (this.sliderValueLabel) {
                this.sliderValueLabel.classList.remove('value-set');
                this.sliderValueLabel.classList.add('value-unset');
            }
        } else {
            // Although a slider can't be set outside its range, we want the number input to follow the same constraints
            if (event && event.type === 'change') {
                if (newValue < this.min) {
                    fixedValue = this.min;
                } else if (newValue > this.max) {
                    fixedValue = this.max;
                }
            }
            this.value = fixedValue; // this.value is internal only, does not affect controller value unless read externally
            if (this.slider) {
                // Set the slider input value
                this.slider.value = fixedValue;
                this.slider.classList.remove('value-unset');
                this.slider.classList.add('value-set');
            }
            if (this.input) {
                // Set the number text input value
                this.input.value = fixedValue;
                this.input.classList.remove('value-unset');
                this.input.classList.add('value-set');
            }
            if (this.clearButton) {
                this.clearButton.classList.remove('value-unset');
                this.clearButton.classList.add('value-set');
            }
            if (this.sliderValueLabel) {
                this.sliderValueLabel.classList.remove('value-unset');
                this.sliderValueLabel.classList.add('value-set');
            }
        }

        /*
         * Call the change function if the value has changed and we have an event,
         *   suggesting it came from the event handler and not called directly.
         * Per CAR-230 we call change before setting the label,
         *   because if the label is customized it might need access to the updated value in the controller,
         *   or it could influence the value of other properties (ex. weight<->bmi)
         */
        if (oldValue !== fixedValue) {
            this.callChangeFunction(event);
        }

        this.setSliderValueLabel(fixedValue);

        return this.value;
    }

    setSliderValueLabel(displayValue = this.value) {
        if (this.sliderValueLabel) {
            // Fire an event to allow customization of the sliderValueLabel.innerHTML (there might be a better way to do this)
            const cancelled = !this.events.dispatchEvent(new ManagerEvent('setSliderValueLabel', {cancelable: true}, {
                sliderValueLabel: this.sliderValueLabel,
                labelPrefix: this.labelPrefix,
                labelSuffix: this.labelSuffix,
                emptyLabel: this.emptyLabel,
                value: this.value,
                disabled: this.disabled,
            }));

            if (!cancelled) {
                // Set the floating value label
                if (this.value === undefined) {
                    if (this.disabled) {
                        this.sliderValueLabel.innerHTML = this.disabledLabel || this.emptyLabel;
                    } else {
                        this.sliderValueLabel.innerHTML = this.emptyLabel;
                    }
                } else {
                    this.sliderValueLabel.innerHTML = `${this.labelPrefix}${displayValue}${this.labelSuffix}`;
                }
            }
        }
    }

    setChangeFunction(newChangeFunction) {
        this.changeFunction = newChangeFunction;
    }

    callChangeFunction(event) {
        if (this.changeFunction !== undefined) {
            this.changeFunction(this.value, event);
        }
    }

    clear(event) {
        this.setValue(undefined, event);
    }

    handleSliderClicked(event) {
        if (this.value === undefined) {
            this.setValue(this.slider.value, event);
        }
    }

    handleSliderChanged(event) {
        this.setValue(this.slider.value, event);
    }

    handleInputChanged(event) {
        if (this.input.value !== undefined && this.input.value !== '') {
            this.setValue(this.input.value, event);
        } else {
            this.clear(event);
        }
    }

    handleClearButtonClicked(event) {
        this.clear(event);
    }

    // public method for controller to enable and disable UI controls
    enableControls(enable) {
        this.disabled = !enable;
        if (this.input) {
            this.input.disabled = !enable;
        }
        if (this.slider) {
            this.slider.disabled = !enable;
        }
        this.setSliderValueLabel();
    }

}
